import { useEffect, useRef, useState } from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import apiService from '../services/ApiService';
import resolveHubBaseUrl from '../utils/hubBaseUrl';

const CARD_FIELDS = [
  'totalLogins',
  'totalPageViews',
  'totalChatsStarted',
  'totalAssistantsOpened',
  'totalBoardsStarted',
  'totalCallsStarted',
  'totalForumReplies',
  'totalFilesDownloaded',
  'totalSolicitudesCreacion',
  'totalSolicitudesEdicion',
  'totalEventos'
];
const FLASH_DURATION_MS = 2200;
const ALERT_DURATION_MS = 3200;
const REFRESH_DEBOUNCE_MS = 450;

const getHubBaseUrl = () =>
  resolveHubBaseUrl();

const normalizeGuid = (value) => (value || '').toString().trim().toLowerCase();

const buildAlertText = (payload) => {
  const label = payload?.etiquetaEvento || payload?.tipoEvento || 'métricas';
  return `Se actualizó ${label.toLowerCase()}.`;
};

const getChangedFields = (previousStats, nextStats) => {
  if (!previousStats || !nextStats) return [];
  return CARD_FIELDS.filter((field) => Number(previousStats?.[field] || 0) !== Number(nextStats?.[field] || 0));
};

const useMetricsRealtime = ({
  enabled,
  scope = 'global',
  colegioId = '',
  stats,
  loadStats,
  shouldHandleEvent
}) => {
  const [highlightedCards, setHighlightedCards] = useState({});
  const [liveAlert, setLiveAlert] = useState(null);
  const statsRef = useRef(stats);
  const loadStatsRef = useRef(loadStats);
  const shouldHandleEventRef = useRef(shouldHandleEvent);
  const connectionRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const alertTimerRef = useRef(null);
  const flashTimersRef = useRef({});

  useEffect(() => {
    statsRef.current = stats;
  }, [stats]);

  useEffect(() => {
    loadStatsRef.current = loadStats;
  }, [loadStats]);

  useEffect(() => {
    shouldHandleEventRef.current = shouldHandleEvent;
  }, [shouldHandleEvent]);

  useEffect(() => {
    if (!enabled) return undefined;

    let disposed = false;
    const hubBase = getHubBaseUrl();
    const connection = new HubConnectionBuilder()
      .withUrl(`${hubBase}/hubs/metricas`, {
        accessTokenFactory: () => apiService.getTokenFromCookies() || '',
        withCredentials: true
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    const clearTimers = () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      if (alertTimerRef.current) {
        clearTimeout(alertTimerRef.current);
        alertTimerRef.current = null;
      }
      Object.values(flashTimersRef.current).forEach((timerId) => clearTimeout(timerId));
      flashTimersRef.current = {};
    };

    const markChangedFields = (fields, payload) => {
      if (!fields.length) return;

      setHighlightedCards((prev) => {
        const next = { ...prev };
        fields.forEach((field) => {
          next[field] = true;
        });
        return next;
      });

      fields.forEach((field) => {
        if (flashTimersRef.current[field]) {
          clearTimeout(flashTimersRef.current[field]);
        }

        flashTimersRef.current[field] = setTimeout(() => {
          setHighlightedCards((prev) => ({ ...prev, [field]: false }));
          delete flashTimersRef.current[field];
        }, FLASH_DURATION_MS);
      });

      setLiveAlert({
        id: `${payload?.eventId || payload?.fechaUtc || Date.now()}`,
        text: buildAlertText(payload)
      });

      if (alertTimerRef.current) {
        clearTimeout(alertTimerRef.current);
      }

      alertTimerRef.current = setTimeout(() => {
        setLiveAlert(null);
      }, ALERT_DURATION_MS);
    };

    const queueRefresh = (payload) => {
      if (typeof shouldHandleEventRef.current === 'function' && !shouldHandleEventRef.current(payload)) {
        return;
      }

      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      refreshTimerRef.current = setTimeout(async () => {
        const previousStats = statsRef.current;
        const nextStats = await loadStatsRef.current?.({
          showLoader: false,
          silentError: true
        });

        if (disposed || !nextStats) return;

        const changedFields = getChangedFields(previousStats, nextStats);
        markChangedFields(changedFields, payload);
      }, REFRESH_DEBOUNCE_MS);
    };

    const joinGroups = async () => {
      if (scope === 'colegio') {
        if (!normalizeGuid(colegioId)) return;
        await connection.invoke('JoinColegioMetrics', colegioId);
        return;
      }

      await connection.invoke('JoinGlobalMetrics');
    };

    connection.on('TelemetryMetricsUpdated', queueRefresh);
    connection.onreconnected(async () => {
      try {
        await joinGroups();
      } catch (err) {
        console.warn('No se pudo reenganchar el hub de métricas', err);
      }
    });

    (async () => {
      try {
        await connection.start();
        if (disposed) {
          await connection.stop();
          return;
        }
        await joinGroups();
        connectionRef.current = connection;
      } catch (err) {
        console.warn('No se pudo conectar al hub de métricas', err);
      }
    })();

    return () => {
      disposed = true;
      clearTimers();
      const activeConnection = connectionRef.current || connection;
      activeConnection.stop().catch(() => {});
      connectionRef.current = null;
    };
  }, [enabled, scope, colegioId]);

  return {
    highlightedCards,
    liveAlert
  };
};

export const metricsEventMatchesColegio = (payload, colegioId) =>
  !normalizeGuid(colegioId) || normalizeGuid(payload?.idColegio) === normalizeGuid(colegioId);

export default useMetricsRealtime;
